import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiService } from '~/services/apiService';
import classNames from 'classnames';
import styles from './RegisterNickModal.module.css';

interface RegisterNickModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultNick?: string;
  defaultGender?: 'male' | 'female' | string;
  chatUserId?: string;
}

const initialFormState = {
  nick: '',
  email: '',
  password: '',
  gender: ''
};

function RegisterNickModal({ isOpen, onClose, defaultNick = '', defaultGender = '', chatUserId }: RegisterNickModalProps) {
  const [formData, setFormData] = useState(initialFormState);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        nick: defaultNick.slice(0, 30),
        email: '',
        password: '',
        gender: defaultGender || ''
      });
      setErrors([]);
      setShowPassword(false);
    }
  }, [isOpen, defaultNick, defaultGender]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const el = overlayRef.current;
      if (!el) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.left = `${vv.offsetLeft}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
      el.style.width = `${vv.width}px`;
      el.style.height = `${vv.height}px`;
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const validate = () => {
    const newErrors: string[] = [];

    if (!formData.nick || formData.nick.length > 30) newErrors.push('nick');

    if (!formData.email) {
      newErrors.push('email');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) newErrors.push('email');
    }

    if (!formData.password || formData.password.length < 4) newErrors.push('password');
    if (!formData.gender) newErrors.push('gender');

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const registerData = {
        nick: formData.nick,
        email: formData.email,
        password: formData.password,
        gender: formData.gender,
        ...(chatUserId ? { chatUserId } : {})
      };

      const response = await apiService('/auth/register', 'POST', registerData);

      if (response.status === 201) {
        toast.success(response.message, { position: 'top-center', autoClose: false });
        sessionStorage.setItem('pendingActivationLogin', JSON.stringify({
          emailOrNick: registerData.nick || registerData.email,
          password: registerData.password
        }));
        onClose();
        return;
      }

      if (response.status === 409) {
        if (response.field) {
          setErrors([response.field]);
        } else {
          setErrors(['nick', 'email']);
        }
        toast.warning(response.message, { position: 'top-center' });
        return;
      }
    } catch (error: any) {
      console.error('Krytyczny błąd połączenia lub serwera:', error);
      toast.error('Wystąpił problem z połączeniem z serwerem. Spróbuj ponownie później.', {
        position: 'top-center'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div ref={overlayRef} className={styles.modalOverlay}>
      <div ref={modalRef} className={styles.authModal}>
        <button type="button" className={styles.modalCloseBtn} onClick={onClose}>✕</button>

        <div className={styles.modalTitle}>Rejestracja nicka</div>

        <form className={styles.authForm} onSubmit={handleSubmit} noValidate>
          <div className={styles.formScroll}>
            <div className={styles.inputGroup}>
              <div className={styles.labelWithCounter}>
                <label className={errors.includes('nick') ? styles.labelError : ''}>Nick</label>
                <span className={classNames(styles.charCounter, { [styles.counterError]: formData.nick.length >= 30 })}>
                  <span className={styles.charCounterInfo}>{formData.nick.length}/30</span>
                </span>
              </div>
              <input
                type="text"
                maxLength={30}
                className={errors.includes('nick') ? styles.inputError : ''}
                value={formData.nick}
                onChange={(e) => setFormData({ ...formData, nick: e.target.value.slice(0, 30) })}
                placeholder="Twój nick..."
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={errors.includes('email') ? styles.labelError : ''}>E-mail</label>
              <input
                type="email"
                className={errors.includes('email') ? styles.inputError : ''}
                value={formData.email}
                onChange={(e) => {
                  const noSpaces = e.target.value.replace(/\s/g, '');
                  setFormData({ ...formData, email: noSpaces });
                }}
                placeholder="E-mail"
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={errors.includes('password') ? styles.labelError : ''}>
                Hasło (min. 4 znaki)
              </label>
              <div className={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={errors.includes('password') ? styles.inputError : ''}
                  value={formData.password}
                  onChange={(e) => {
                    const noSpaces = e.target.value.replace(/\s/g, '');
                    setFormData({ ...formData, password: noSpaces });
                  }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={errors.includes('gender') ? styles.labelError : ''}>Płeć</label>
              <div className={styles.genderGrid}>
                <button
                  type="button"
                  className={`${styles.genderBtn} ${formData.gender === 'female' ? styles.genderBtnActive : ''}`}
                  onClick={() => setFormData({ ...formData, gender: 'female' })}
                >
                  Kobieta {formData.gender === 'female' && ' ✓'}
                </button>
                <button
                  type="button"
                  className={`${styles.genderBtn} ${formData.gender === 'male' ? styles.genderBtnActive : ''}`}
                  onClick={() => setFormData({ ...formData, gender: 'male' })}
                >
                  Mężczyzna {formData.gender === 'male' && ' ✓'}
                </button>
              </div>
            </div>
          </div>

          <button type="submit" className={styles.submitAuthBtn}>
            ZAREJESTRUJ NICK
          </button>
        </form>
      </div>
    </div>
  );
}

export default RegisterNickModal;
